import * as THREE from 'three';
import { registerSW } from 'virtual:pwa-register';
import { IP } from './strings/ip';
import { LEX } from './strings/lexicon';
import { GameRenderer } from './render/renderer';
import { Terrain, createWater } from './render/terrain';
import { grassTile, waterTile } from './render/textures';
import { CreatureRenderer } from './render/creatures';
import { ItemRenderer } from './render/items';
import { DayNight } from './render/dayNight';
import { createCameraRig } from './input/camera';
import { GroundPicker } from './input/verbs';
import { attachGestures } from './input/gestures';
import { startLoop } from './app/loop';
import { SimClient } from './app/simClient';
import { watchContextLoss } from './app/contextLost';
import { DebugOverlay } from './debug/overlay';
import { DebugPanel } from './debug/panel';
import { AudioEngine } from './audio/engine';
import { Sfx } from './audio/sfx';
import { ChirpSynth } from './audio/chirps';
import { MusicBox } from './audio/musicBox';
import { AmbientBed } from './audio/ambient';
import { injectBaseStyles } from './ui/win94';
import { Tray, type Tool } from './ui/tray';
import { Hud } from './ui/hud';
import { showToast } from './ui/toast';
import { awayLines, showPostcard } from './ui/postcard';
import { GlyphAtlas } from './ui/glyphs';
import { Conlang } from './app/conlang';
import type { SpeechContext } from './app/speech';
import { VERSION } from './version';
import { Ticker } from './ui/ticker';
import { Bubbles } from './ui/bubbles';
import { showProfileCard } from './ui/profileCard';
import { showLogbook } from './ui/logbook';
import { Speech } from './app/speech';
import { BehaviorCensus } from './app/census';
import { unpackUtter } from './sim/meta/language';
import { terrainHeight, configureTerrain } from './sim/terrain';
import { runBootSequence } from './boot/bootSequence';
import { DiscoveryDirector } from './boot/discoveryDirector';
import { EV, type SimEvent } from './sim/protocol';
import { ITEM } from './sim/items';
import { loadBestSave, putSave, wipeSaves } from './save/db';
import { showInspectCard } from './ui/inspect';
import { expandMini, writeMini } from './save/snapshotLS';
import { requestPersistOnce } from './save/persistPerm';
import { offerInstallOnce, watchInstallPrompt } from './platform/installPrompt';
import type { AwayReport, SaveV1 } from './save/schema';

document.title = IP.GAME_TITLE;
watchInstallPrompt();

const appEl = document.getElementById('app') as HTMLDivElement;
const canvas = document.getElementById('gl') as HTMLCanvasElement;
const hudEl = document.getElementById('hud') as HTMLDivElement;
const gameRenderer = new GameRenderer(canvas);

// ---- scene (sync) ----
const scene = new THREE.Scene();
scene.background = new THREE.Color('#79B7E8');
// fog only at the far horizon, so a full zoom-out still sees the whole island
scene.fog = new THREE.Fog('#79B7E8', 160, 360);

const sun = new THREE.DirectionalLight('#FFF4D6', 2.4);
sun.position.set(18, 30, 12);
scene.add(sun);
const hemi = new THREE.HemisphereLight('#BBDCFF', '#4E7A3A', 1.0);
scene.add(hemi);

// art v2 is the adopted default: pixel-textured ground/water, cuter
// creatures, chunkier internal resolution (?art=1 keeps the legacy look
// around for comparisons)
const artV2 = new URL(location.href).searchParams.get('art') !== '1';
const waterMap = artV2 ? waterTile() : null;

// terrain is seed-specific — built in start() once the world seed is known
let terrain: Terrain;

const creatures = new CreatureRenderer(undefined, artV2);
scene.add(creatures.group);
const items = new ItemRenderer();
scene.add(items.group);
const dayNight = new DayNight(scene, sun, hemi);
if (artV2) gameRenderer.setRenderHeight(390);

const camera = new THREE.PerspectiveCamera(25, gameRenderer.aspect, 0.5, 400);
const rig = createCameraRig(camera, canvas);
rig.setLookAt(0, 34, 50, 0, 0.5, 0, false);

window.addEventListener('resize', () => {
  camera.aspect = gameRenderer.aspect;
  camera.updateProjectionMatrix();
});

async function start(): Promise<void> {
  // ---- load ----
  const url = new URL(location.href);
  const debugPop = Number(url.searchParams.get('pop') ?? '0') | 0;
  const fresh = debugPop > 0 || url.searchParams.has('fresh');
  let save: SaveV1 | null = null;
  if (!fresh) save = (await loadBestSave()) ?? expandMini();
  const seed = save?.seed ?? url.searchParams.get('seed') ?? 'alpha';

  // shape this seed's island (coastline, ponds, biomes) before building the
  // mesh — the worker configures the identical terrain from the same seed
  configureTerrain(seed);
  terrain = new Terrain(artV2 ? grassTile() : null);
  scene.add(terrain.mesh, createWater(waterMap));

  // debug mode + persistent pause must be known before the sim boots: a
  // paused world starts frozen and skips the away-time simulation entirely
  const debugEnabled =
    import.meta.env.DEV ||
    location.search.includes('debug') ||
    localStorage.getItem('tg.debug') === '1';
  if (!debugEnabled) localStorage.removeItem('tg.paused'); // no lurking freezes
  const startPaused = debugEnabled && localStorage.getItem('tg.paused') === '1';

  const sim = new SimClient(seed, debugPop, save, Date.now(), startPaused);

  // ---- audio + ui ----
  const audio = new AudioEngine();
  const sfx = new Sfx(audio);
  const chirps = new ChirpSynth(audio);
  const music = new MusicBox(audio);
  const ambient = new AmbientBed(audio);

  // Audio unlock. On touchscreens only pointerup/touchend/click count as a
  // user activation — pointerdown does NOT — so we retry on every qualifying
  // event until the context actually runs, then play proof of life once.
  let audioLive = false;
  const tryUnlock = (): void => {
    if (audioLive) return;
    audio.unlock();
    void audio.context.resume().then(() => {
      if (!audioLive && audio.context.state === 'running') {
        audioLive = true;
        sfx.powerOn();
        ambient.start(); // from here the island is never silent
        for (const ev of UNLOCK_EVENTS) window.removeEventListener(ev, tryUnlock, true);
      }
    });
  };
  const UNLOCK_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'] as const;
  for (const ev of UNLOCK_EVENTS) {
    window.addEventListener(ev, tryUnlock, { capture: true, passive: true });
  }

  // CRT pass: on by default, off under reduced-motion, toggleable
  let crtOn =
    localStorage.getItem('tg.crt') !== '0' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  gameRenderer.setCrt(crtOn);

  injectBaseStyles();
  const hud = new Hud(hudEl);

  // custom names the player has given individual creatures (id -> name),
  // persisted independently of the world save so they survive rebuilds
  const customNames = new Map<number, string>();
  try {
    const raw = localStorage.getItem('tg.names');
    if (raw) for (const [k, v] of Object.entries(JSON.parse(raw) as Record<string, string>)) {
      customNames.set(Number(k), v);
    }
  } catch {
    // ignore malformed name store
  }
  const setName = (id: number, name: string): void => {
    if (name) customNames.set(id, name);
    else customNames.delete(id);
    try {
      localStorage.setItem('tg.names', JSON.stringify(Object.fromEntries(customNames)));
    } catch {
      // storage full / private mode — name still applies this session
    }
  };

  // a tool's how-to, shown as a prominent toast the first time it's armed
  // (and any time via the HELP button). Tap the toast to dismiss.
  const showToolHint = (tool: Tool, force = false): void => {
    const key = `tg.tip.${tool}`;
    if (!force && localStorage.getItem(key) === '1') return;
    localStorage.setItem(key, '1');
    showToast(hudEl, LEX.toolHint(tool), undefined, undefined, 'toolhint');
  };
  const tray = new Tray(hudEl, (tool) => {
    sfx.uiTap();
    showToolHint(tool);
  });
  const picker = new GroundPicker(camera, canvas);
  const director = new DiscoveryDirector(sim, tray, save?.onboarding);

  // ---- meta layer: their language, their file on you ----
  const glyphs = new GlyphAtlas(seed);
  const conlang = new Conlang(seed); // this world's own invented words
  const speech = new Speech(glyphs, conlang);
  const speechCtx = (): SpeechContext => ({ stage: sim.stats.stage, pop: sim.stats.pop });
  const ticker = new Ticker(hudEl);
  const projV = new THREE.Vector3();
  const bubbles = new Bubbles(hudEl, (x, y, z) => {
    projV.set(x, y, z).project(camera);
    return {
      px: ((projV.x + 1) / 2) * window.innerWidth,
      py: ((-projV.y + 1) / 2) * window.innerHeight,
      visible: projV.z < 1 && Math.abs(projV.x) < 1.1 && Math.abs(projV.y) < 1.1,
    };
  });
  const census = new BehaviorCensus(save?.meta?.discovered);

  const profileData = () => {
    const s = sim.stats;
    const descriptors: string[] = [];
    descriptors.push(s.trust > 0.62 ? 'TRUSTED KEEPER' : s.trust < 0.35 ? 'STRANGER STILL' : 'GENTLE HAND');
    descriptors.push(s.attachment > 0.55 ? 'ALWAYS RETURNS' : s.attachment < 0.22 ? 'SUDDEN VISITOR' : 'COMES AND GOES');
    descriptors.push(s.curiosity > 0.62 ? 'FULL OF SURPRISES' : 'STEADY HAND');
    descriptors.push(s.fairness > 0.66 ? 'LOVES ALL EQUALLY' : 'HAS FAVORITES');
    const footer =
      s.stage >= 4
        ? 'WE UNDERSTAND YOU NOW. WE LIKE YOU ANYWAY.'
        : s.stage >= 3
          ? 'THEY THINK THEY UNDERSTAND YOU.'
          : 'THEY ARE STUDYING YOU.';
    return {
      title: LEX.profileTitle(),
      descriptors,
      footer,
      stats: [
        { label: 'TRUST', value01: s.trust },
        { label: 'BOND', value01: s.attachment },
        { label: 'WONDER', value01: s.curiosity },
        { label: 'EVENNESS', value01: s.fairness },
      ],
      portraitSeed: seed,
    };
  };

  // top-right menu chips
  const menu = document.createElement('div');
  menu.style.cssText =
    'position:fixed;top:calc(6px + env(safe-area-inset-top));right:6px;display:flex;gap:6px;pointer-events:auto;z-index:20;';
  const mkMenuBtn = (label: string, onTap: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.className = 'w94-btn';
    b.textContent = label;
    b.style.cssText = 'height:34px;padding:0 10px;font-size:10px;pointer-events:auto;';
    b.addEventListener('click', () => {
      sfx.uiTap();
      onTap();
    });
    menu.appendChild(b);
    return b;
  };
  mkMenuBtn('?', () => showToolHint(tray.armed, true)); // re-show the armed tool's how-to
  mkMenuBtn('FILE', () => showProfileCard(hudEl, profileData()));
  mkMenuBtn('LOG', () => showLogbook(hudEl, LEX.logbookTitle(), census.entries()));
  const crtBtn = mkMenuBtn('CRT', () => {
    crtOn = !crtOn;
    localStorage.setItem('tg.crt', crtOn ? '1' : '0');
    gameRenderer.setCrt(crtOn);
    crtBtn.classList.toggle('w94-armed', !crtOn);
  });
  crtBtn.classList.toggle('w94-armed', !crtOn);
  // sound toggle: pressed-in (sunken) = muted, and unmuting is audible
  let muted = localStorage.getItem('tg.mute') === '1';
  if (muted) audio.setMasterVolume(0);
  const soundBtn = mkMenuBtn('SOUND', () => {
    muted = !muted;
    localStorage.setItem('tg.mute', muted ? '1' : '0');
    audio.setMasterVolume(muted ? 0 : 0.8);
    soundBtn.classList.toggle('w94-armed', muted);
    if (!muted) window.setTimeout(() => sfx.powerOn(), 60); // hear it come back
  });
  soundBtn.classList.toggle('w94-armed', muted);
  mkMenuBtn('RESET', () => showResetDialog());
  hudEl.appendChild(menu);

  const showResetDialog = (): void => {
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'position:fixed;inset:0;display:grid;place-items:center;z-index:45;pointer-events:auto;background:rgba(0,0,0,0.3);';
    const win = document.createElement('div');
    win.className = 'w94-raised';
    win.style.cssText = 'min-width:230px;max-width:80vw;padding:2px;';
    const bar = document.createElement('div');
    bar.textContent = LEX.resetTitle();
    bar.style.cssText =
      'background:linear-gradient(90deg,var(--w94-title-a),var(--w94-title-b));color:#fff;font:bold 12px/1 monospace;padding:5px 8px;letter-spacing:1px;';
    const body = document.createElement('div');
    body.style.cssText = 'padding:12px;font:11px/1.8 monospace;color:var(--w94-text);white-space:pre-wrap;';
    body.textContent = LEX.resetLines().join('\n');
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;justify-content:center;padding:0 10px 12px;flex-wrap:wrap;';
    const mk = (label: string, onTap: () => void): void => {
      const b = document.createElement('button');
      b.className = 'w94-btn';
      b.textContent = label;
      b.style.cssText = 'min-width:80px;height:34px;padding:0 10px;font-size:10px;';
      b.addEventListener('click', onTap);
      row.appendChild(b);
    };
    mk(LEX.resetYes(), () => {
      void wipeSaves()
        .catch(() => {})
        .then(() => {
          localStorage.removeItem('tg.mini');
          localStorage.removeItem('tg.names'); // names were tied to the old world's creatures
          localStorage.removeItem('tg.paused'); // a new world starts running
          const newSeed = Math.random().toString(36).slice(2, 10);
          location.href = `${location.pathname}?seed=${newSeed}`;
        });
    });
    const debugOn = localStorage.getItem('tg.debug') === '1';
    mk(debugOn ? 'DEBUG OFF' : 'DEBUG ON', () => {
      localStorage.setItem('tg.debug', debugOn ? '0' : '1');
      location.reload();
    });
    mk(LEX.resetNo(), () => wrap.remove());
    win.append(bar, body, row);
    wrap.appendChild(win);
    hudEl.appendChild(wrap);
  };

  // the Song asks things, rarely, assembling a fresh question each time
  const maybeAsk = (force = false): boolean => {
    if (!force && (document.hidden || !bootDone)) return false;
    if (ticker.asking) return false;
    const stage = sim.stats.stage;
    if (stage < 1) return false;
    const last = Number(localStorage.getItem('tg.lastQ') ?? '0');
    if (!force && (Date.now() - last < 8 * 60_000 || Math.random() < 0.5)) return false;
    const q = speech.question(stage, true, speechCtx());
    localStorage.setItem('tg.lastQ', String(Date.now()));
    ticker.ask(q.frag, {
      a: q.a,
      b: q.b,
      onAnswer: (choice) => {
        sim.answer(q.qid, choice);
        ticker.print(speech.utterance(Math.max(2, stage), 7 /* GRATEFUL */, 42, true, speechCtx()));
      },
    });
    return true;
  };
  window.setInterval(() => void maybeAsk(), 30_000);

  attachGestures({
    root: appEl,
    canvas,
    picker,
    getTool: () => tray.armed,
    creaturesMesh: creatures.mesh,
    population: () => sim.population,
    sim,
    sfx,
    onToolUsed: (tool) => {
      if (tool === 'ball') director.markPlayed();
    },
    onInspect: (id) => sim.inspect(id),
    onCall: () => chirps.motif(7), // the player's stable call sign
    onSong: () => {
      chirps.motif(7);
      window.setTimeout(() => chirps.motif(11), 500);
      window.setTimeout(() => chirps.motif(7), 1000);
    },
    onRub: (() => {
      let lastPurr = 0;
      return (id: number) => {
        const now = performance.now();
        if (now - lastPurr > 900) {
          lastPurr = now;
          chirps.purr(id * 37 + 11, 0.9);
        }
      };
    })(),
  });
  sim.onInspect = (data) =>
    showInspectCard(hudEl, data, {
      customName: customNames.get(data.id),
      onRename: (name) => setName(data.id, name),
    });

  // ---- persistence ----
  sim.onSave = (payload) => {
    payload.onboarding = director.flags;
    if (payload.meta) payload.meta.discovered = census.discoveredIds;
    void putSave(payload);
    writeMini(payload);
  };
  window.setInterval(() => {
    if (!document.hidden) sim.requestSave(Date.now());
  }, 5000);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      sim.requestSave(Date.now());
      audio.suspend();
    } else {
      audio.resume();
    }
  });

  sim.onGrid = (grid) => terrain.updateGround(grid);

  let awayReport: AwayReport | null = null;
  let bootDone = false;
  sim.onAway = (report) => {
    if (bootDone) maybeShowPostcard(report);
    else awayReport = report;
  };
  const maybeShowPostcard = (report: AwayReport): void => {
    if (report.elapsedH > 0.15 && report.popBefore > 0) {
      showPostcard(hudEl, LEX.awayTitle(), awayLines(report));
    }
  };

  // ---- service worker update flow ----
  const updateSW = registerSW({
    onNeedRefresh() {
      showToast(hudEl, LEX.updateReady(), LEX.updateAction(), () => {
        sim.requestSave(Date.now());
        window.setTimeout(() => void updateSW(true), 400);
      });
    },
  });

  // ---- sim events -> sound, wobbles, onboarding ----
  const eventLog: SimEvent[] = [];
  sim.onEvents = (events: SimEvent[]) => {
    if (import.meta.env.DEV) {
      eventLog.push(...events);
      if (eventLog.length > 300) eventLog.splice(0, eventLog.length - 300);
    }
    director.handleEvents(events);
    const observedNow = census.processEvents(events);
    if (observedNow.length > 0 && bootDone) {
      showToast(hudEl, LEX.observed(observedNow[0] as string));
    }
    for (const [kind, x, z, a, b] of events) {
      switch (kind) {
        case EV.EGG_CRACK: {
          sfx.eggCrack(a as 1 | 2);
          const egg = sim.findItem(ITEM.EGG);
          if (egg) items.wobble(egg.id);
          break;
        }
        case EV.HATCH:
          sfx.hatch();
          window.setTimeout(() => chirps.motif(a), 350);
          break;
        case EV.EAT:
          sfx.crunch();
          break;
        case EV.APPLE_LAND:
          sfx.thock();
          break;
        case EV.PLOP:
          sfx.plop();
          break;
        case EV.SCRUB_POP:
          sfx.sparklePop();
          break;
        case EV.BOING:
          sfx.boing(a / 255);
          break;
        case EV.CHIRP:
          chirps.chirp(a, { mood: b / 255 });
          break;
        case EV.PET:
          chirps.purr(a, 1.3); // the satisfied rumble
          window.setTimeout(() => chirps.chirp(a, { mood: 0.9, syllables: 1 }), 900);
          break;
        case EV.WAKE_PROGRESS:
          // a muffled, sleepy note from inside the thinning moss
          chirps.chirp(a, { mood: 0.15 + (b / 255) * 0.5, syllables: 1 });
          break;
        case EV.SONG:
          sfx.sparklePop(); // the dance takes
          break;
        case EV.SPLASH:
          sfx.plop(); // a watery blorp
          if (Math.random() < 0.4) chirps.chirp(a, { mood: 0.9, syllables: 1 });
          break;
        case EV.MITOSIS: {
          sfx.sparklePop();
          chirps.motif(b);
          window.setTimeout(() => chirps.motif(a), 420);
          requestPersistOnce(); // the moment of investment
          music.start(); // the world earns its music
          offerInstallOnce(
            (text, label, onAction) => showToast(hudEl, text, label, onAction),
            LEX.installAsk(),
            LEX.installAction(),
            LEX.installIosHint(),
          );
          break;
        }
        case EV.DORMANT:
          chirps.chirp(a, { mood: 0.05, syllables: 1 });
          break;
        case EV.WAKE:
          sfx.sparklePop();
          window.setTimeout(() => chirps.motif(a), 200);
          break;
        case EV.BUILT:
          sfx.hatch();
          showToast(hudEl, LEX.built(a));
          break;
        case EV.UTTER: {
          const { stage, intent } = unpackUtter(b);
          const ctx = speechCtx();
          ticker.print(speech.utterance(stage, intent, a, true, ctx));
          bubbles.say(
            a,
            x,
            terrainHeight(x, z) + 1.95,
            z,
            speech.utterance(stage, intent, a, false, ctx),
            4.5,
          );
          chirps.chirp(a, { mood: 0.8, syllables: Math.min(4, 1 + stage) });
          break;
        }
      }
    }
  };

  // ---- boot ----
  void runBootSequence(
    hudEl,
    {
      studioLine: `${IP.STUDIO.toUpperCase()} (TM) SYSTEM`,
      loadingLine: `LOADING ${IP.GAME_TITLE.toUpperCase()} V1.0`,
      title: IP.GAME_TITLE,
      touchLine: 'TOUCH TO BEGIN',
      graphicUrl: 'opening.png', // dropped into public/; absent = wordmark only
    },
    () => tryUnlock(),
  ).then(() => {
    bootDone = true;
    rig.setLookAt(7, 8.5, 12.5, 0, 0.5, 0, true);
    if (startPaused) showToast(hudEl, 'PAUSED — WORLD FROZEN (DEBUG)', undefined, undefined, 'paused');
    if (awayReport) maybeShowPostcard(awayReport);
    if (sim.population >= 2) music.start(); // returning worlds already earned it
    // the hand is the default tool nobody ever taps — introduce it once,
    // as a prominent toast a few seconds after the world settles
    if (localStorage.getItem('tg.tip.hand') !== '1') {
      window.setTimeout(() => showToolHint('hand'), 5000);
    }
  });

  // ---- frame loop ----
  const overlay = debugEnabled ? new DebugOverlay(hudEl) : null;
  if (debugEnabled) {
    new DebugPanel(
      hudEl,
      {
        warp: (mult) => sim.warp(mult),
        skip: (hours) => sim.skip(hours),
        spawn: (count) => sim.setPopulation(sim.population + count),
        setPaused: (paused) => {
          localStorage.setItem('tg.paused', paused ? '1' : '0');
          if (paused) sim.pause();
          else sim.resume();
        },
      },
      startPaused,
    );
    const ver = document.createElement('div');
    ver.textContent = VERSION;
    ver.style.cssText = [
      'position:fixed',
      'right:4px',
      'bottom:calc(2px + env(safe-area-inset-bottom))',
      'font:10px/1 monospace',
      'color:var(--w94-terminal)',
      'background:var(--w94-terminal-bg)',
      'padding:2px 4px',
      'opacity:0.8',
      'pointer-events:none',
      'z-index:26',
    ].join(';');
    hudEl.appendChild(ver);
  }
  const info = gameRenderer.renderer.info;
  if (overlay) info.autoReset = false;

  let slowFrames = 0;
  let degraded = false;

  let lastStatsTick = -1;
  const tick = (dt: number): void => {
    if (overlay) info.reset();
    // belt-and-suspenders against missed resize events (PWA restore,
    // rotation timing, pre-layout construction): cheap compare per frame
    if (camera.aspect !== gameRenderer.aspect) {
      camera.aspect = gameRenderer.aspect;
      camera.updateProjectionMatrix();
    }
    rig.update(dt);
    sim.applyTo(creatures, items, performance.now(), dt);
    creatures.update(dt);
    bubbles.update(dt);
    dayNight.apply(sim.dayPhase);
    hud.setPopulation(sim.population);
    music.setPopulation(sim.population);
    if (waterMap) {
      waterMap.offset.x += dt * 0.006; // lazy current
      waterMap.offset.y += dt * 0.0025;
    }
    if (sim.stats.tick !== lastStatsTick) {
      lastStatsTick = sim.stats.tick;
      const fresh = census.processStats(sim.stats);
      if (fresh.length > 0 && bootDone) showToast(hudEl, LEX.observed(fresh[0] as string));
    }
    gameRenderer.render(scene, camera);
    if (!degraded) {
      slowFrames = dt > 0.026 ? slowFrames + 1 : 0;
      if (slowFrames > 90) {
        degraded = true;
        gameRenderer.setRenderHeight(400);
      }
    }
    if (overlay) {
      overlay.frame(dt);
      overlay.update({
        pop: sim.stats.pop,
        'sim tick': sim.stats.tick,
        'sim ms': sim.stats.tickMs,
        'snap age': Math.round(sim.snapshotAgeMs),
        calls: info.render.calls,
        tris: info.render.triangles,
        day: sim.dayPhase,
        dormant: sim.countLife(4),
      });
    }
  };

  const loop = startLoop(tick);
  watchContextLoss(canvas, loop, hudEl);

  if (import.meta.env.DEV) {
    // dev tooling hooks: freeze the loop, step frames in hidden tabs (where
    // rAF never fires), grab pixel-exact frames, poke the scene
    const w = window as unknown as Record<string, unknown>;
    w.__loop = loop;
    w.__step = (dt = 1 / 60, n = 1) => {
      for (let i = 0; i < n; i++) tick(dt);
    };
    w.__capture = () => gameRenderer.captureDataUrl();
    w.__shot = (name = 'frame') =>
      fetch(`/__capture?name=${name}`, { method: 'POST', body: gameRenderer.captureDataUrl() }).then(
        (r) => r.status,
      );
    w.__game = { gameRenderer, scene, camera, creatures, items, terrain, rig, sim, tray, audio };
    w.__speech = speech;
    w.__conlang = conlang;
    w.__speechCtx = speechCtx;
    w.__events = eventLog;
    w.__ask = () => maybeAsk(true);
  }
}

void start();
